import React, { useCallback, useEffect, useMemo } from 'react';
import { useRootStore } from 'store';
import { useApolloClient } from '@apollo/client';
import {
  TablePaginatedV8,
  CellLeftV8,
  EditCellV8,
  TableCellV8,
  ButtonNewRecord,
  promiseModalDialog,
  toastSuccess,
} from 'motion-components';
import { useTranslation } from 'react-i18next';
import { generatePath, Link, useHistory, useLocation } from 'react-router-dom';
import { useFormikContext } from 'formik';
import { getRouteByName } from 'app/routes';
import * as Lo from 'lodash';
import { promiseModalSelectMultiCollectionOrders } from 'shared-pages/components';
import {
  CollectionOrderStates,
  RouteLinesStates,
  RouteStates,
} from 'shared-pages/common/constants';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  FormInput,
  gqlDeleteRoutesLine,
  gqlGetCollectionOrdersByIds,
  gqlUpdate,
  RouteLineInput,
} from '../graphql';

import DeleteCellV8 from 'shared-pages/components/buttons/delete-cell-v8';
import { removeTypename } from 'shared-pages/utils/helpers';
import { RouteStatusBadge } from '../route-status-badge';
import ButtonNewCollection from 'shared-pages/components/buttons/button-new-collection';
interface IRoutesLine {
  aa?: number;
  id?: number;
  collectionOrderId?: number;
  isActive?: boolean;
  notes?: string;
  statusId?: number;
  collectionOrder?: {
    code: string;
    collectionPointId: number;
    id: number;
    notes: string;
    collectionpoint: {
      name: string;
      addressArea: string;
      addressCity: string;
      addressDistrict: string;
      addressLine1: string;
      addressLine2: string;
      addressNomos: string;
      id: number;
    };
  };
}
/* ----------- Component ------------------ */
export const RouteLinesTable = ({ isNew, id, statusId, collector }: any) => {
  const location = useLocation<any>();
  const client = useApolloClient();

  const history = useHistory();
  const { appStore, tableStore } = useRootStore();
  const { t } = useTranslation();
  const { values, setFieldValue, errors, resetForm } = useFormikContext<{
    routesLines: IRoutesLine[];
  }>();

  const [getCollectionOrders] = useLazyQuery(gqlGetCollectionOrdersByIds);

  const [updateRoute] = useMutation(gqlUpdate);
  const [deleteRoutesLine] = useMutation(gqlDeleteRoutesLine);
  /**
   * Δημιουργία Νέας Συλλογής
   */
  //handleNewCollection function push history to /collection/-1 and pass state with the collectionOrderId
  const handleNewCollection = useCallback(
    ({ collectionOrderId, routeLineId }: { collectionOrderId: number; routeLineId: number }) => {
      history.push('/collection/-1', { collectionOrderId, routeLineId });
    },
    [id, history], // Dependency array ensures `history` reference is stable
  );
  // Define Columns (Without Weight)
  const columns = useMemo(
    () => [
      {
        id: 'aa',
        header: t('Αα'),
        columnName: t('Αα'),
        accessorKey: 'aa',
        orderByField: 'aa',
        cell: TableCellV8,
        size: 2,
        meta: { type: 'text' },
      },
      {
        id: 'collectionOrder.code',
        header: t('Αίτημα Συλλογής'),
        columnName: t('Αίτημα Συλλογής'),
        accessorKey: 'collectionOrder.code',
        orderByField: 'collectionOrder.code',
        cell: ({ row }: any) => {
          const id = row.original.collectionOrderId;
          const code = row.original.collectionOrder.code;
          const path = generatePath(getRouteByName('Order'), { id: id });
          const notes = row.original.collectionOrder.notes;
          return (
            <div>
              <Link to={path}>{code}</Link>
              {notes && <div style={{ color: 'gray', fontSize: 'smaller' }}>{notes}</div>}
            </div>
          );
        },

        size: 7,
      },
      {
        id: 'collectionOrder.collectionpoint.name',
        header: t('Σημείο Συλλογής'),
        columnName: t('Σημείο Συλλογής'),
        accessorKey: 'collectionOrder.collectionpoint.name',
        orderByField: 'collectionOrder.collectionpoint.name',
        cell: ({ row }: any) => {
          const id = row.original.collectionOrder?.collectionPointId;
          const name = row.original.collectionOrder?.collectionpoint?.name;
          const path = generatePath(getRouteByName('Collectionpoint'), { id: id });
          return (
            <div>
              <Link to={path}>{name}</Link>
            </div>
          );
        },
        size: 20,
      },
      {
        id: 'collectionOrder.collectionpoint.addressLine1',
        header: t('Διεύθυνση'),
        columnName: t('Διεύθυνση'),
        accessorKey: 'collectionOrder.collectionpoint.addressLine1',
        orderByField: 'collectionOrder.collectionpoint.addressLine1',
        cell: CellLeftV8,
        size: 15,
      },
      {
        id: 'collectionOrder.collectionpoint.addressArea',
        header: t('Περιοχή'),
        columnName: t('Περιοχή'),
        accessorKey: 'collectionOrder.collectionpoint.addressArea',
        orderByField: 'collectionOrder.collectionpoint.addressArea',
        cell: CellLeftV8,
        size: 10,
      },
      {
        id: 'collectionOrder.collectionpoint.addressCity',
        header: t('Πόλη'),
        columnName: t('Πόλη'),
        accessorKey: 'collectionOrder.collectionpoint.addressCity',
        orderByField: 'collectionOrder.collectionpoint.addressCity',
        cell: CellLeftV8,
        size: 10,
      },
      {
        id: 'collectionOrder.collectionpoint.addressDistrict',
        header: t('Νομός'),
        columnName: t('Νομός'),
        accessorKey: 'collectionOrder.collectionpoint.addressDistrict',
        orderByField: 'collectionOrder.collectionpoint.addressDistrict',
        cell: CellLeftV8,
        size: 10,
      },
      {
        id: 'state.id',
        header: t('Kατάσταση'),
        columnName: t('Kατάσταση'),
        accessorKey: 'state.name',
        orderByField: 'state.name',
        cell: ({ row }) => {
          return (
            <>
              {row.original.id !== null && (
                <div className="text-center">
                  <RouteStatusBadge
                    id={row.original?.state?.id}
                    name={row?.original?.state?.name}
                  />

                  {row.original?.statusId === RouteLinesStates.Executed &&
                    row?.original?.collections && (
                      <div className="mt-1">
                        <Link to={`/collection/${row?.original?.collections[0]?.id}`}>
                          {row?.original?.collections[0]?.code}
                        </Link>
                      </div>
                    )}
                </div>
              )}
            </>
          );
        },
        size: 15,
      },
      //need to add inside edit cell the delete button
      {
        id: 'edit',
        header: t('Ενέργειες'),
        columnName: t('Ενέργειες'),
        accessorKey: 'edit',
        size: 120,
        cell: (props: any) => {
          const { row, table } = props;
          // console.log(table.options.meta?.editedRows[row.id]);
          // console.log('this Id', row.original.id);
          return true ? (
            <span className="d-flex px-2 ">
              {statusId !== RouteStates.Executed && // Ολοκληρωμένο Δρομολόγιο
                row.original.statusId !== RouteLinesStates.Executed && // Ολοκληρωμένη Γραμμή Δρομολόγιου
                row.original.id !== null && // not is new
                EditCellV8({ row, table })}
              {statusId !== RouteStates.Executed && // Ολοκληρωμένο Δρομολόγιο
                row.original.statusId !== RouteLinesStates.Executed && ( // Ολοκληρωμένη Γραμμή Δρομολόγιου
                  <DeleteCellV8 className="ms-2" row={row} table={table} />
                )}
              {!table.options.meta?.editedRows[row.id] &&
                statusId !== RouteStates.Executed && // Ολοκληρωμένο Δρομολόγιο
                row.original.statusId !== RouteLinesStates.Executed && // Ολοκληρωμένη Γραμμή Δρομολόγιου
                row.original.id !== null && (
                  <ButtonNewCollection
                    label="Συλλογή"
                    className="d-flex ms-2"
                    onClick={() =>
                      handleNewCollection({
                        collectionOrderId: row.original.collectionOrderId,
                        routeLineId: row.original.id,
                      })
                    }
                    disabled={isNew}
                    size="sm"
                  />
                )}
            </span>
          ) : (
            <>-</>
          );
        },
      },
    ],
    [t],
  );

  // Keep optimisticData in sync with linesData, but not during reset

  // Save Row Logic (Without Weight Calculations)
  const onSaveRow = async (rowIndex, row, changes) => {
    console.log('onSaveRow', rowIndex, row, changes);
    // Copy of the existing Formik data
    let lcRoutesLines = JSON.parse(JSON.stringify(values?.routesLines || []));

    // Update aa if changes exist,parse int to avoid string comparison and update the optimistic data
    if (changes.aa) {
      setFieldValue(`routesLines[${rowIndex}].aa`, parseInt(changes.aa));
      lcRoutesLines[rowIndex].aa = changes.aa;
    }
    lcRoutesLines.sort((a, b) => a.aa - b.aa);

    // Calculate Total Quantity

    return true; // Return success
  };

  /* Προσθήκη Αιτημάτων Συλλογής */

  const fetchCollectionOrdersByIds = async (ids: number[]) => {
    console.log('Fetching collection orders by IDs:', ids);
    if (!ids || ids.length === 0) {
      console.warn('No IDs provided for fetching collection orders');
      return [];
    }

    const whereClause = JSON.stringify({
      id: { $in: ids },
    });
    console.log('Where clause:', whereClause);

    try {
      console.log('Fetching collection orders with where clause:', whereClause);
      const result = await client.query({
        query: gqlGetCollectionOrdersByIds,
        variables: { where: whereClause }, // ✅ correct variable name
        fetchPolicy: 'network-only',
      });

      const rows = result?.data?.getCollectionOrders?.data ?? []; // ✅ use result, not Lo.result
      console.log('Collection Orders fetched:', rows);

      if (!Array.isArray(rows)) {
        console.warn('Fetched data is not an array:', rows);
        return [];
      }
      return rows;
    } catch (error) {
      console.error('Error fetching collection orders:', error);
      throw error;
    }
  };
  const addNewOrdersBtnClicked = useCallback(async () => {
    const initFilters = {
      isActive: true,
      statusId: CollectionOrderStates.NotRouted,
      collectorId: collector?.id,
    };

    const res = await promiseModalSelectMultiCollectionOrders({
      initFilters,
      appStore,
      title: t('Επιλογή Αιτημάτων Συλλογής'),
      collector,
    });
    console.log('Selected Collection Orders:', res);
    if (!res?.length) return;

    // ✅ always guard with fallback array
    let updatedRoutesLines = (values?.routesLines ?? []).map((r, index) => ({
      ...r,
      aa: index + 1,
    }));
    console.log('Updated Routes Lines:', updatedRoutesLines);
    // If your modal already returns the fields you render, you can drop the fetch.
    // If not, keep your fetch, but still guard with ?? [].
    const selectedOrderIds = res.map((o) => o.id);
    const fetchedOrders: any = await fetchCollectionOrdersByIds(selectedOrderIds);
    console.log('Fetched Orders:', fetchedOrders);
    const safeOrders = Array.isArray(fetchedOrders) ? fetchedOrders : [];

    const newRouteLines = safeOrders.map((order, index) => ({
      id: null,
      aa: updatedRoutesLines.length + index + 1,
      collectionOrderId: order.id,
      statusId: RouteLinesStates.Planned,
      collectionOrder: {
        id: order.id,
        code: order.code,
        collectionPointId: order.collectionPointId,
        collectionpoint: {
          name: order.collectionpoint?.name,
          addressLine1: order.collectionpoint?.addressLine1,
          addressLine2: order.collectionpoint?.addressLine2,
          addressArea: order.collectionpoint?.addressArea,
          addressCity: order.collectionpoint?.addressCity,
          addressNomos: order.collectionpoint?.addressNomos,
          addressDistrict: order.collectionpoint?.addressDistrict,
        },
      },
    }));
    console.log('New Route Lines:', newRouteLines);

    const merged = [...updatedRoutesLines, ...newRouteLines].map((l, i) => ({ ...l, aa: i + 1 }));
    // ✅ commit once
    console.log('Merged Routes Lines:', merged);
    setFieldValue('routesLines', merged, false);
  }, [appStore, collector, t, setFieldValue, values?.routesLines]);

  return (
    <>
      <div
        className="d-flex form-group-title justify-content-between"
        style={{ marginBottom: '0px' }}
      >
        <h5 className="align-self-center">{t('Σημεία Συλλογής του Δρομολογίου')}</h5>
        {
          <ButtonNewRecord
            className="align-self-center me-3"
            label="Αιτήματα Συλλογής"
            onClick={addNewOrdersBtnClicked}
            disabled={statusId === 2}
          />
        }
      </div>

      {/* TableGrid */}
      {values?.routesLines ? (
        <TablePaginatedV8
          loading={false}
          id="route-lines-grid"
          appStore={appStore}
          tableStore={tableStore}
          key="grid"
          columns={columns as any}
          onSaveRow={onSaveRow}
          deleteRow={async (row, index) => {
            console.log('Delete Row:', row);
            try {
              // If new line (undefined or null `id`):
              if (!row?.id) {
                const lines = [...values.routesLines];
                if (index > -1) lines.splice(index, 1);
                setFieldValue('routesLines', lines);
                return true;
              }

              // Confirm deletion
              const ans = await promiseModalDialog({
                title: 'Διαγραφή Εγγραφής',
                text: `<div>Θέλετε να διαγράψετε τη γραμμή <strong>${row?.aa}</strong> (${row?.collectionOrder?.collectionpoint?.name});</div>`,
              });
              if (!ans || !row.id) return false;

              // Call deleteRoutesLine mutation
              await deleteRoutesLine({ variables: { id: row.id } }).catch((reason) => {
                console.error('Error deleting route line:', reason);
                throw new Error('Failed to delete route line.');
              });

              // Remove the row locally and recalculate 'aa'
              const updatedRoutesLines = (values?.routesLines ?? [])
                .filter((r) => r.id !== row.id)
                .map((r, index) => ({ ...r, aa: index + 1 })) as Array<
                IRoutesLine & { aa: number }
              >;

              // Update Formik state and optimistic data
              setFieldValue('routesLines', updatedRoutesLines);

              // Prepare data for backend update
              const route = removeTypename({ ...values, routesLines: updatedRoutesLines });
              //remove collectionOrder from routesLines
              route.routesLines = updatedRoutesLines.map((r) => ({
                ...Lo.pick(r, Lo.keys(new RouteLineInput())),
              })) as any;
              //reshape route to remove state and driver
              const mutRoute = Lo.pick(route, Lo.keys(new FormInput()));
              console.log('Updated Route:', mutRoute);
              // Update the route in the backend
              const d = await updateRoute({ variables: { data: mutRoute } });

              // Notify success
              toastSuccess(`Επιτυχημένη Διαγραφή Εγγραφής [${d.data[Object.keys(d.data)[0]].id}]`);
              resetForm({
                values: {
                  ...values,
                  routesLines: updatedRoutesLines,
                },
              });
              return true;
            } catch (error) {
              console.error('Error deleting row:', error);
              return false;
            } finally {
              // Refetch data after deletion
            }
          }}
          data={values?.routesLines ?? []}
          dataCount={values?.routesLines.length ?? 0}
          topBorderRadius={false}
          initSorting={[{ id: 'aa', desc: false }]}
          hidePagination={true}
        />
      ) : (
        <div style={{ height: '200px' }}></div>
      )}
    </>
  );
};
